import { PostStatus, queryClient } from "@/constants";
import { CLIENT_API } from "@/services/axios-client";
import { useNotificationStore } from "@/states/zustand/notification";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useGetPosts = (page: number) => {
    const { data, isLoading, ...rest } = useQuery({
        queryKey: ["posts", page],
        queryFn: () => CLIENT_API.getPosts(page),
        select: (data) => ({
            ...data,
            content: data.content.map(post => ({
                coverImage: post?.images?.[0]?.url || null,
                ...post
            }))
        })
    });

    return {
        data,
        isLoading,
        ...rest
    }
}

export const useCreatePost = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["createPost"],
        mutationFn: ({ status, data }: { status: PostStatus, data: FormData }) => CLIENT_API.createPost(status, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["posts"]
            });
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Post created successfully!",
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error ?? "An error occurred while creating the post",
            });
        }
    });

    return {
        mutate,
        isCreating: isPending,
        ...rest
    }
}

export const useDeletePosts = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["deletePost"],
        mutationFn: (data: { ids: number[] }) => CLIENT_API.deletePosts(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["posts"]
            });
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Posts deleted successfully!",
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error ?? "An error occurred while deleting posts!",
            });
        }
    });

    return {
        mutate,
        isDeleting: isPending,
        ...rest
    }
}

export const usePublishPost = () => {

    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["publishPost"],
        mutationFn: (id: number) => CLIENT_API.publishPost(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["posts"]
            });
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Post published successfully!",
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error ?? "An error occurred while publishing the post",
            });
        }
    });

    return {
        mutate,
        isPublishing: isPending,
        ...rest
    }
}

export const useUpdatePost = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["updatePost"],
        mutationFn: (data: { id: number, status: PostStatus, formData: FormData }) => CLIENT_API.updatePost(data.id, data.status, data.formData),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["posts"]
            });
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Post updated successfully!",
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error ?? "An error occurred while updating the post",
            });
        }
    });

    return {
        mutate,
        isUpdating: isPending,
        ...rest
    }
}